import { useState, useEffect } from 'react';
import { storeApi, auctionApi } from '@/utils/api';
import { useStore } from '@/store/useStore';
import { CardRenderer } from '@/components/Cards/CardRenderer';
import { SkeletonCard } from '@/components/Feedback/SkeletonCard';
import { ShoppingBag } from 'lucide-react';

export const MyListingsTab = ({ onNavigate }: { onNavigate: (path: string, payload?: any) => void }) => {
    const { user } = useStore();
    const [listings, setListings] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user?.telegram_id) return;
        setLoading(true);

        Promise.allSettled([
            storeApi.getListings(user.telegram_id, 1, 50, 'my_listings').then(res => res.json()),
            auctionApi.getActive(user.telegram_id, 'my_auctions').then(res => res.json())
        ]).then(([listingsRes, auctionsRes]) => {
            let combined: any[] = [];
            
            if (listingsRes.status === 'fulfilled' && Array.isArray(listingsRes.value)) {
                // Ensure they are marked for MARKET context
                const marketItems = listingsRes.value.map(i => ({...i, type: 'MARKET'}));
                combined = [...combined, ...marketItems];
            }
            if (auctionsRes.status === 'fulfilled' && auctionsRes.value.success && Array.isArray(auctionsRes.value.auctions)) {
                // Normalize auction items for consistent rendering with AuctionHouse
                const auctionItems = auctionsRes.value.auctions.map((a: any) => {
                    let finalName = a.seller_name ? `@${a.seller_name}` : 'User';
                    if (a.verified_username) finalName = a.verified_username;
                    else if (a.name && a.name.startsWith('@')) finalName = a.name;

                     return {
                        ...a,
                        type: 'AUCTION' as const,
                        id: a.card_id, // Base Card ID for templates
                        auctionId: a.id, // Auction Instance ID
                        target_username: a.target_username || a.card_username || a.card_name || a.username || a.name,
                        ownerName: a.seller_name || 'User',
                        displayName: finalName,
                        price: parseFloat(a.current_bid || '0'),
                        profileImage: a.image_filename
                            ? (a.image_filename.startsWith('/card_Image') ? a.image_filename : `/card_Image/${a.image_filename}`)
                            : undefined,
                        ownerImage: a.seller_avatar,
                        waveColor: a.base_color || (a.listing_data?.wave_color) || '#3390EC',
                        textColor: a.text_color || (a.listing_data?.text_color) || '#FFFFFF',
                        showTimer: true,
                        timerHours: a.timeLeftSeconds ? a.timeLeftSeconds / 3600 : (a.duration_hours || 0),
                        isNft: (a.asset_type === 'nft' || a.assetType === 'nft'),
                        isActiveAuction: true,
                        isPending: (a.isPending || a.bid_count === 0),
                        likesCount: a.likes_count || 0,
                        isLiked: !!a.is_liked
                    };
                });
                combined = [...combined, ...auctionItems];
            }
            setListings(combined);
        }).catch(console.error)
          .finally(() => setLoading(false));

    }, [user?.telegram_id]);

    if (loading) {
        return (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 px-4 mt-6">
                {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
        );
    }

    if (listings.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 opacity-50 px-4 text-center mt-6">
                <ShoppingBag size={48} className="mb-4 text-white/30" />
                <h3 className="text-lg font-bold text-white mb-2">No Active Listings</h3>
                <p className="text-sm text-white/60 max-w-[250px]">You don't have any items listed on the market right now.</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 px-4 mt-6">
            {listings.map(item => (
                <CardRenderer 
                    key={`${item.type}-${item.id}-${item.auctionId || ''}`} 
                    {...item}
                    context={item.type === 'AUCTION' ? 'auction' : 'market'} 
                    variant="premium"
                    showCardIcon={true}
                    compactOwner={true}
                    onInteract={() => onNavigate(


                        item.type === 'AUCTION' ? 'auction_bid' : 'card_view', 
                        item.type === 'AUCTION' ? (item.auctionId || item.id) : `list_${item.id}`
                    )} 
                />
            ))}
        </div>
    );
};

